import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaEdit } from "react-icons/fa";
import authService from '../../../utils/authService';
import { fetchAllCourses } from "../../../utils/courseUtils";
import { fetchBatchesByCourse } from "../../../utils/batchUtils";

interface TrainerData {
  id: string;
  user_first_name: string;
  user_last_name: string;
  mobile_no: string;
  email_id: string;
  enabled?: boolean;
  del_row?: boolean;
}

interface TrainerFormData {
  id: string;
  user_first_name: string;
  user_last_name: string;
  mobile_no: string;
  email_id: string;
}

interface Batch {
  batch_name: string;
  batch_id: string;
}

interface Course {
  course_id: string;
  course_name: string;
}

const emptyForm = (): TrainerFormData => ({
  id: "",
  user_first_name: "",
  user_last_name: "",
  mobile_no: "",
  email_id: "",
});

const trainerDisplayName = (trainer: TrainerData): string =>
  `${trainer.user_first_name || ""} ${trainer.user_last_name || ""}`.trim();

const Trainer: React.FC = () => {
  const location = useLocation();
  const batchId = location.state?.batch_id ? location.state.batch_id : "";
  const batchName = location.state?.batch_name ? location.state.batch_name : "";
  const courseId = location.state?.course_id ? location.state.course_id : "";
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [formData, setFormData] = useState<TrainerFormData>(emptyForm());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [trainers, setTrainers] = useState<TrainerData[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState(courseId);
  const [selectedBatch, setSelectedBatch] = useState(batchId);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [currentBatchTrainers, setCurrentBatchTrainers] = useState<TrainerData[]>([]);

  useEffect(() => {
    if (!batchId || !courseId || !batchName) {
      navigate("/batches");
    }
    setLoading(true);
    fetchData();
  }, [batchId, courseId]);

  useEffect(() => {
    if (selectedBatch) {
      setLoading(true);
      fetchSelectedBatchTrainers();
    }
  }, [selectedBatch]);

  useEffect(() => {
    if (selectedCourse !== "") {
      setLoading(true);
      fetchBatches();
    }
  }, [selectedCourse]);

  const fetchData = async () => {
    try {
      await Promise.all([fetchTrainers(), fetchCourses()]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const data = await fetchBatchesByCourse(selectedCourse);
      setBatches(data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const data = await fetchAllCourses();
      setCourses(data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await authService.get("admin/get_all_trainer/");
      setTrainers(response.data.trainers || []);
    } catch (error) {
      console.error("Error fetching trainers:", error);
    }
  };

  const fetchSelectedBatchTrainers = async () => {
    try {
      const json = { batch_id: selectedBatch };
      const response = await authService.post("admin/get_trainer_for_batch/", json, {
        headers: { "Content-Type": "application/json" },
      });
      setCurrentBatchTrainers(response.data.trainers || []);
    } catch (error) {
      console.error("Error fetching selected batch trainers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleModal = (type: string, trainer?: TrainerData): void => {
    setModalType(type);
    if (type === "new") {
      setFormData(emptyForm());
    } else if (trainer) {
      setFormData({
        id: trainer.id,
        user_first_name: trainer.user_first_name || "",
        user_last_name: trainer.user_last_name || "",
        mobile_no: trainer.mobile_no || "",
        email_id: trainer.email_id || "",
      });
    }
    setShowModal(true);
    setError("");
  };

  const handleSave = async (): Promise<void> => {
    if (!formData.user_first_name || !formData.mobile_no || !formData.email_id) {
      setError("First name, mobile number and email are required");
      return;
    }
    const isEmailTaken = trainers.some(trainer =>
      (trainer.email_id.toLowerCase() === formData.email_id.toLowerCase()) &&
      (trainer.id !== formData.id)
    );
    if (isEmailTaken) {
      setError("The Email is not available");
      return;
    }
    setError("");

    try {
      const json = {
        id: formData.id,
        user_first_name: formData.user_first_name,
        user_last_name: formData.user_last_name,
        mobile_no: formData.mobile_no,
        email_id: formData.email_id,
      };
      setLoading(true);
      await authService.post("admin/create_trainer/", json, {
        headers: { "Content-Type": "application/json" },
      });
      fetchTrainers();
      setShowModal(false);
    } catch (error) {
      setError("Error saving Trainer");
      console.error("Error saving Trainer:", error);
    } finally {
      setLoading(false);
    }
  };

  const enableForBatch = async (trainer: TrainerData): Promise<void> => {
    if (trainer && selectedBatch) {
      try {
        setLoading(true);
        const json = { trainer_id: trainer.id, batch_id: selectedBatch };
        await authService.post("admin/enable_trainer_for_batch/", json, {
          headers: { "Content-Type": "application/json" },
        });
        fetchSelectedBatchTrainers();
      } catch (error) {
        console.error("Error enabling for batch:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const addToBatch = async (trainer: TrainerData): Promise<void> => {
    if (trainer && selectedBatch) {
      try {
        setLoading(true);
        const json = { trainer_id: trainer.id, batch_id: selectedBatch };
        await authService.post("admin/add_trainers_to_batch/", json, {
          headers: { "Content-Type": "application/json" },
        });
        fetchSelectedBatchTrainers();
      } catch (error) {
        console.error("Error adding to batch:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const isAssigned = (trainerId: string): boolean =>
    currentBatchTrainers.some((selectedTrainer) => selectedTrainer.id === trainerId);

  const isEnabled = (trainerId: string): boolean =>
    currentBatchTrainers.some(
      (selectedTrainer) => selectedTrainer.id === trainerId && selectedTrainer.enabled
    );

  return (
    <>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}

      <div className="border pt-3 px-2 rounded-2 bg-white my-2 me-2" style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}>
        <div className="d-flex justify-content-between ">
          <span className="d-flex justify-content-start align-items-center ps-4">
            <label className='me-2'>Course</label>
            <Form.Select style={{width:"150px"}} className="me-3" onChange={(e) => { setSelectedCourse(e.target.value); setSelectedBatch(''); }} value={selectedCourse}>
              {courses.map((course) => (
                <option key={course.course_id} value={course.course_id}>
                  {course.course_name}
                </option>
              ))}
            </Form.Select>
            <label className='me-2'>Batch</label>
            <Form.Select className="me-3" style={{width:"150px"}} onChange={(e) => setSelectedBatch(e.target.value)} value={selectedBatch}>
              <option value="" key="">Select batch</option>
              {batches.map((batch) => (
                <option key={batch.batch_id} value={batch.batch_id}>
                  {batch.batch_name}
                </option>
              ))}
            </Form.Select>
          </span>
          <button className="btn" onClick={() => { handleModal("new"); }}>
            + New Trainer
          </button>
        </div>
        <div className="p-4">
          <Table className='border-top mt-2'>
            <thead style={{ borderBottom: '1px solid black', borderTop: '1px solid black' }}>
              <tr>
                <td>Name</td>
                <td>Mobile No</td>
                <td>Email</td>
                <td>Add Trainer to Batch</td>
                <td>Enable for Batch</td>
                <td>Action</td>
              </tr>
            </thead>
            <tbody>
              {trainers.filter(trainer => !trainer.del_row).map((trainer) => (
                <tr key={trainer.id}>
                  <td>{trainerDisplayName(trainer)}</td>
                  <td>{trainer.mobile_no}</td>
                  <td>{trainer.email_id}</td>
                  <td className="ms-3">
                   {selectedBatch !='' && <input
                      type="checkbox"
                      checked={isAssigned(trainer.id)}
                      onChange={() => addToBatch(trainer)}
                    />}
                  </td>
                  <td className="ms-3">
                  {selectedBatch !='' && isAssigned(trainer.id) ? (
                      <input
                        type="checkbox"
                        checked={isEnabled(trainer.id)}
                        onChange={() => enableForBatch(trainer)}
                      />
                    ) : (
                      <span></span>
                    )}
                  </td>
                  <td>
                    <FaEdit className="fs-6 me-2" role="button" title="Edit" onClick={() => { handleModal("edit", trainer); }} style={{ cursor: "pointer" }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{modalType === "new" ? "Add New Trainer" : "Edit Trainer"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <p className="text-danger">{error}</p>}
            <Form>
              <Form.Group controlId="formTrainerFirstName" className="d-flex align-items-center justify-content-between">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                style={{
                  width: "50%"
                }}
                  type="text"
                  name="user_first_name"
                  value={formData.user_first_name}
                  onChange={(e) => setFormData({ ...formData, user_first_name: e.target.value })}
                />
              </Form.Group>

              <Form.Group controlId="formTrainerLastName" className="mt-3 d-flex align-items-center justify-content-between">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                style={{
                  width: "50%"
                }}
                  type="text"
                  name="user_last_name"
                  value={formData.user_last_name}
                  onChange={(e) => setFormData({ ...formData, user_last_name: e.target.value })}
                />
              </Form.Group>

              <Form.Group controlId="formTrainerMobile" className="mt-3 d-flex align-items-center justify-content-between">
                <Form.Label>Mobile No</Form.Label>
                <Form.Control
                style={{
                  width: "50%"
                }}
                  type="number"
                  name="mobile_no"
                  value={formData.mobile_no}
                  onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
                />
              </Form.Group>

              <Form.Group controlId="formTrainerEmail" className="mt-3 d-flex align-items-center justify-content-between">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  style={{
                    width: "50%"
                  }}
                  name="email_id"
                  value={formData.email_id}
                  onChange={(e) => setFormData({ ...formData, email_id: e.target.value })}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default Trainer;
